import type { ElectronApi } from '../shared/electron-api';
import { bindSiteMenus, bindWebsiteMotion } from './website';
import { bindGuest } from './guest';
import {
  showGuestWaiting,
  updateGuestWaiting,
  hideGuestWaiting,
  showGuestRecovery,
  showConnectionRecovery,
  hideConnectionRecovery,
} from '../shared/ui/guest-waiting';
import type { RemoteCursorMessage } from '../shared/datachannel';
import {
  ControllerSession,
  type RemotePeerInfo,
} from '../renderer/controller';
import { attachTouch } from './touch';
import { attachPointer } from './pointer';
import { attachKeyboard } from './keyboard';
import { bindFullscreenButton } from '../shared/fullscreen';
import {
  $,
  api,
  appState,
  readTheme,
  readStoredStatsOverlay,
  readStoredRemoteStreamSettings,
  saveStatsOverlay,
  saveRemoteStreamSettings,
  platformLabel,
} from './app-state';
import {
  applyTheme,
  saveTheme,
  bindSystemIconTheme,
} from './theme';
import {
  showView,
  updateConnectionPath,
  updateConnectionQuality,
  setSessionStatsOverlayEnabled,
  updateAppliedStreamSettings,
  setSessionControlsHidden,
  armSessionChromeAutoHide,
  closeStreamSettings,
  openStreamSettings,
  trapStreamSettingsFocus,
  applyStreamSettings,
  updateStreamSettingsButton,
  bindSessionChromeAutoHide,
  revealSessionChrome,
  setSessionConnectionStalled,
  syncSessionVideoVisibility,
  disconnect,
  copySessionDiagnostics,
} from './session';
import {
  clearPendingRemoteClipboard,
  updateRemoteClipboardButton,
  copyRemoteClipboard,
} from './clipboard';
import {
  onFileTransfer,
  syncPersistedFileTasks,
} from './file-transfer';
import { bindFileTransfer } from './file-transfer-bind';
import {
  sendInput,
  updateShortcutButton,
  pasteClipboardAndTriggerRemote,
  bindKeyboard,
} from './keybindings';
import { bindTextSend } from './text-send';
import {
  lanConnect,
  renderLanHistory,
  deleteLanHistory,
  loadLanHistory,
  fillAddrDatalist,
} from './lan';
import {
  accountLogin,
  accountRegister,
  accountLogout,
  deleteAccount,
  loadAuthSessions,
  loadSecurityActivity,
  revokeOtherAuthSessions,
} from './account';
import {
  loadDevices,
  scheduleAccountValidation,
  restoreSession,
} from './devices';
import { bindSessionModePicker } from '../shared/ui/session-mode';
import type { ConnectionState } from '../shared/connection-state';
import { normalizeRemoteStreamSettings, type RemoteStreamSettings } from '../shared/stream-profile';
import { setConnectionFirstFrameTime, setConnectionInputRtt, setConnectionQualityPanelOpen, toggleConnectionQualityPanel } from '../shared/ui/connection-quality';
import { evaluateConnectionPreflight, type ConnectionPreflightResult } from '../shared/connection-preflight';

// 类型标注仅用于 callbacks 的对象字面量，保持与原单体模块逐字一致。
type DownloadChannel = 'stable' | 'preview';
interface DownloadManifestItem { file: string; version?: string; label?: string; }
interface DownloadManifest {
  version: string;
  channel?: DownloadChannel;
  displayName?: string;
  notice?: string;
  downloads: Record<string, DownloadManifestItem>;
}

const downloadManifests = new Map<DownloadChannel, DownloadManifest>();

function parseDownloadManifest(value: unknown, expectedChannel: DownloadChannel): DownloadManifest | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<DownloadManifest>;
  if (!/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/.test(String(raw.version || ''))) return null;
  if (!raw.downloads || typeof raw.downloads !== 'object' || Array.isArray(raw.downloads)) return null;
  if (raw.channel != null && raw.channel !== expectedChannel) return null;
  const downloads: Record<string, DownloadManifestItem> = {};
  for (const [platform, item] of Object.entries(raw.downloads)) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const candidate = item as DownloadManifestItem;
    // The browser follows a public manifest, so only allow a single file name.
    // This keeps a malformed or replaced manifest from escaping the download
    // directory through a path segment.
    if (
      typeof candidate.file !== 'string' ||
      candidate.file.length < 1 ||
      candidate.file.length > 180 ||
      candidate.file !== candidate.file.split(/[\\/]/).pop() ||
      candidate.file === '.' ||
      candidate.file === '..'
    ) continue;
    downloads[platform] = { file: candidate.file, version: candidate.version, label: candidate.label };
  }
  if (!Object.keys(downloads).length) return null;
  return {
    version: String(raw.version),
    channel: expectedChannel,
    displayName: typeof raw.displayName === 'string' ? raw.displayName : undefined,
    notice: typeof raw.notice === 'string' ? raw.notice : undefined,
    downloads,
  };
}

async function fetchDownloadManifest(channel: DownloadChannel): Promise<DownloadManifest | null> {
  try {
    const path = channel === 'preview'
      ? './downloads/preview/manifest.json'
      : './downloads/manifest.json';
    const response = await fetch(path, { cache: 'no-store' });
    if (!response.ok) return null;
    return parseDownloadManifest(await response.json(), channel);
  } catch {
    return null;
  }
}

function updateDownloadChannelUi(channel: DownloadChannel): void {
  const manifest = downloadManifests.get(channel);
  if (!manifest) return;
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-download-channel]')) {
    const selected = button.dataset.downloadChannel === channel;
    button.classList.toggle('active', selected);
    button.setAttribute('aria-pressed', String(selected));
  }
  const prefix = channel === 'preview' ? './downloads/preview/' : './downloads/';
  for (const [platform, item] of Object.entries(manifest.downloads)) {
    const card = document.querySelector<HTMLAnchorElement>(`[data-download-platform="${platform}"]`);
    if (!card) continue;
    card.href = `${prefix}${encodeURIComponent(item.file)}?v=${encodeURIComponent(item.version || manifest.version)}`;
    card.dataset.downloadChannel = channel;
    const version = card.querySelector<HTMLElement>(`[data-download-version="${platform}"]`);
    if (version) version.textContent = `${item.version || manifest.version} · ${item.label || '立即下载'}`;
  }
  const downloads = document.getElementById('downloads');
  if (downloads) downloads.dataset.downloadChannel = channel;
  const status = document.getElementById('download-channel-status');
  if (status) {
    status.hidden = false;
    status.dataset.channel = channel;
    status.textContent = channel === 'preview'
      ? `${manifest.displayName || '体验版'} v${manifest.version} · ${manifest.notice || '用于测试新功能，可能需要手动完成系统授权。'}`
      : `${manifest.displayName || '正式版'} v${manifest.version} · 经发布校验的稳定版本。`;
  }
}

function bindDownloadChannels(): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-download-channel]')) {
    button.addEventListener('click', () => {
      const channel = button.dataset.downloadChannel;
      if (channel === 'stable' || channel === 'preview') updateDownloadChannelUi(channel);
    });
  }
}

async function loadDownloadManifest(): Promise<void> {
  const [stable, preview] = await Promise.all([
    fetchDownloadManifest('stable'),
    fetchDownloadManifest('preview'),
  ]);
  if (stable) downloadManifests.set('stable', stable);
  if (preview) downloadManifests.set('preview', preview);
  if (!downloadManifests.size) return;

  bindDownloadChannels();
  const picker = document.getElementById('download-channel-picker');
  if (picker && preview) picker.classList.remove('hidden');
  // Keep the stable channel as the default even when a preview exists. A user
  // can opt into the test build explicitly, and QA can use ?channel=preview.
  const requested = new URLSearchParams(window.location.search).get('channel');
  const channel: DownloadChannel = requested === 'preview' && preview ? 'preview' : 'stable';
  updateDownloadChannelUi(downloadManifests.has(channel) ? channel : preview ? 'preview' : 'stable');
}

function renderConnectionPreflight(result: ConnectionPreflightResult): void {
  appState.connectionPreflightCanConnect = result.canConnect;
  const root = document.getElementById('connection-preflight');
  const title = document.getElementById('connection-preflight-title');
  const list = document.getElementById('connection-preflight-list');
  if (root) root.dataset.state = result.state;
  if (title) title.textContent = result.summary;
  if (list) {
    list.replaceChildren();
    for (const check of result.checks) {
      const item = document.createElement('li');
      item.className = 'connection-preflight-item';
      item.dataset.state = check.state;
      const label = document.createElement('strong');
      label.textContent = check.label;
      const detail = document.createElement('span');
      detail.textContent = check.detail;
      item.append(label, detail);
      list.appendChild(item);
    }
  }
  for (const id of ['guest-connect', 'lan-connect-btn']) {
    const button = document.getElementById(id) as HTMLButtonElement | null;
    if (!button) continue;
    button.disabled = !result.canConnect;
    button.setAttribute('aria-disabled', String(!result.canConnect));
  }
}

function runConnectionPreflight(): void {
  renderConnectionPreflight(evaluateConnectionPreflight({
    online: navigator.onLine,
    secureContext: window.isSecureContext,
    hasWebRtc: typeof RTCPeerConnection === 'function',
    hasClipboard: Boolean(navigator.clipboard && typeof navigator.clipboard.writeText === 'function'),
  }));
}

bindWebsiteMotion();
bindSiteMenus();

const callbacks = {
  onConnectionState: (state: ConnectionState) => {
    const root = $('view-session');
    root.dataset.connectionPhase = state.phase;
    root.dataset.connectionReason = state.reason;
    root.dataset.reconnectAttempt = String(state.reconnectAttempt);
    $('session-status').dataset.phase = state.phase;
    $('session-status').dataset.reason = state.reason;
    if ((state.phase === 'recovering' || state.phase === 'stalled') && appState.video?.srcObject) {
      showConnectionRecovery(state.message || '连接暂时不稳定，正在恢复…', state.reconnectAttempt);
    } else if (state.phase === 'connected' || state.phase === 'failed' || state.phase === 'closed') {
      hideConnectionRecovery();
    }
    if (state.phase === 'recovering' || state.phase === 'stalled' || state.phase === 'failed') revealSessionChrome();
    if (state.phase === 'failed') {
      // 失败可能发生在已经收到画面之后，必须提供明确的恢复入口，不能只
      // 把错误写到状态栏里让用户猜下一步怎么做。
      showGuestWaiting(
        () => appState.cancelSession?.(),
        state.retryable ? () => appState.retrySession?.() : undefined,
      );
      updateGuestWaiting(state.message, 'error');
    }
    if (!state.message || state.phase === 'connected' || state.phase === 'closed') return;
    updateGuestWaiting(state.message, state.phase === 'failed' ? 'error' : 'idle');
    $('session-status').textContent = state.message;
  },
  onStatus: (text: string, state: 'idle' | 'online' | 'error' = 'idle') => {
    updateGuestWaiting(text, state);
    $('session-status').textContent = text === '已接收文件'
      ? '已接收文件，浏览器下载已开始'
      : text;
    if (state === 'error' || /(断开|恢复|无法|失败|没有响应)/.test(text)) revealSessionChrome();
  },
  onStream: (stream: MediaStream) => {
    hideGuestWaiting();
    appState.video!.srcObject = stream;
    appState.video!.muted = false; // 连接按钮已是用户手势，尝试播放远端音频
    appState.video!.focus({ preventScroll: true });
    armSessionChromeAutoHide();
  },
  onDevice: (text: string) => { $('session-peer').textContent = text; },
  onPeerInfo: (info: RemotePeerInfo | null) => {
    appState.remotePlatform = info?.os || 'unknown';
    $('session-peer').textContent = info
      ? `${info.deviceName} · ${platformLabel(info.os)}`
      : '远端设备';
    if (!info) {
      appState.pointerInput?.hide();
      appState.pointerInput?.clearRemoteCursor();
      if (appState.sessionWasConnected) setSessionConnectionStalled(true);
      revealSessionChrome();
    }
    appState.pointerInput?.refresh();
    updateShortcutButton();
  },
  onConnectionPath: updateConnectionPath,
  onConnectionQuality: updateConnectionQuality,
  onFirstFrame: setConnectionFirstFrameTime,
  onInputRtt: setConnectionInputRtt,
  onVideoSettings: updateAppliedStreamSettings,
  onControlReady: (ready: boolean) => {
    if (ready) {
      appState.sessionWasConnected = true;
      armSessionChromeAutoHide();
      setSessionConnectionStalled(false);
      // 连接重新建立后恢复用户上次选择的画面偏好。
      appState.controller?.sendVideoSettings(appState.remoteStreamSettings);
    } else {
      if (appState.sessionWasConnected) setSessionConnectionStalled(true);
      revealSessionChrome();
    }
    $('session-status').textContent = ready
      ? appState.sessionMode === 'view' ? '正在共享屏幕' : '可以开始控制'
      : '连接已断开';
  },
  onRemoteInputReady: (ready: boolean, detail = '') => {
    if (appState.sessionMode === 'view') return;
    $('session-status').textContent = ready ? '远程输入已授权' : detail || '远程输入未授权';
  },
  onRemoteCursor: (cursor: RemoteCursorMessage) => {
    appState.pointerInput?.setRemoteCursor(cursor);
  },
  onRemoteCursorMode: (mode: 'captured' | 'overlay') => {
    appState.pointerInput?.setRemoteCursorMode(mode);
  },
  onConnectionStalled: (stalled: boolean) => {
    setSessionConnectionStalled(stalled);
    if (stalled) {
      $('session-status').textContent = '连接没有响应，请检查网络';
    } else if ($('session-status').textContent === '连接没有响应，请检查网络') {
      $('session-status').textContent = '连接已恢复';
    }
  },
  onReconnectExhausted: () => {
    showGuestRecovery(
      () => appState.cancelSession?.(),
      () => appState.retrySession?.(),
      '连接没有响应，已停止自动重试。',
    );
  },
  onClipboard: (text: string, copied: boolean) => {
    if (copied) {
      clearPendingRemoteClipboard();
      return;
    }
    // 先主动尝试同步到本机。Chromium/Edge 在已授予剪贴板权限的安全
    // 页面中允许这一步直接完成；如果浏览器要求用户手势，再保留内容，
    // 让同一个工具按钮变成“完成复制”。
    const revision = ++appState.remoteClipboardRevision;
    appState.pendingRemoteClipboard = text;
    updateRemoteClipboardButton();
    $('session-status').textContent = '正在同步远端剪贴板…';
    void api.writeClipboard(text).then((written) => {
      if (revision !== appState.remoteClipboardRevision) return;
      if (written) {
        clearPendingRemoteClipboard();
        $('session-status').textContent = '远端内容已同步到本机';
      } else {
        $('session-status').textContent = '远端内容已到达，点击"完成复制"写入本机剪贴板';
      }
    }).catch(() => {
      if (revision === appState.remoteClipboardRevision) {
        $('session-status').textContent = '远端内容已到达，点击"完成复制"写入本机剪贴板';
      }
    });
  },
  onClipboardFiles: (files: { name: string; size: number; kind?: 'file' | 'image' }[], copied: boolean) => {
    if (files.length === 0) return;
    const image = files.length === 1 && files[0].kind === 'image';
    $('session-status').textContent = copied
      ? image ? '已接收远端图片，浏览器剪贴板或下载已完成' : `已接收 ${files.length} 个远端文件，浏览器下载已开始`
      : image ? '已收到远端图片，但浏览器未能保存' : '已收到远端文件，但浏览器未能开始下载';
  },
  onFileTransfer,
};

function newController(): ControllerSession {
  const controller = new ControllerSession(api as unknown as ElectronApi, callbacks);
  controller.setAccessMode(appState.sessionMode);
  return controller;
}
export { newController };

function setWebPreferenceStatus(text: string, state: 'ok' | 'err' | '' = ''): void {
  const status = document.getElementById('web-settings-status');
  if (!status) return;
  status.textContent = text;
  status.className = `status web-settings-status${state ? ` ${state}` : ''}`;
}

function syncWebPreferenceControls(): void {
  const quality = document.getElementById('web-remote-quality') as HTMLSelectElement | null;
  const frameRate = document.getElementById('web-remote-framerate') as HTMLSelectElement | null;
  const stats = document.getElementById('web-stats-overlay') as HTMLInputElement | null;
  if (quality) quality.value = appState.remoteStreamSettings.quality;
  if (frameRate) frameRate.value = String(appState.remoteStreamSettings.frameRate);
  if (stats) stats.checked = appState.sessionStatsOverlay;
  const theme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
  document.querySelectorAll<HTMLButtonElement>('[data-web-theme]').forEach((button) => {
    const active = button.dataset.webTheme === theme;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });
}

function applyWebPreferences(): void {
  const quality = document.getElementById('web-remote-quality') as HTMLSelectElement | null;
  const frameRate = document.getElementById('web-remote-framerate') as HTMLSelectElement | null;
  const stats = document.getElementById('web-stats-overlay') as HTMLInputElement | null;
  if (!quality || !frameRate || !stats) return;
  const settings = normalizeRemoteStreamSettings({
    quality: quality.value,
    frameRate: Number(frameRate.value),
  } as RemoteStreamSettings);
  appState.remoteStreamSettings = settings;
  appState.sessionStatsOverlay = stats.checked;
  saveRemoteStreamSettings(settings);
  saveStatsOverlay(stats.checked);
  setSessionStatsOverlayEnabled(stats.checked);
  const applied = appState.controller?.sendVideoSettings(settings) ?? false;
  setWebPreferenceStatus(applied ? '已保存，正在应用到当前连接' : '已保存，下次连接自动使用', 'ok');
}

// ---------- 事件绑定 ----------
function bindEvents(): void {
  bindGuest();
  bindSessionModePicker('guest-mode-picker');
  bindSessionModePicker('devices-mode-picker');
  bindSessionModePicker('lan-mode-picker');
  $('theme-toggle').addEventListener('click', () => {
    const current = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
    saveTheme(current === 'dark' ? 'light' : 'dark');
    syncWebPreferenceControls();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-web-theme]').forEach((button) => {
    button.addEventListener('click', () => {
      const theme = button.dataset.webTheme;
      if (theme !== 'light' && theme !== 'dark') return;
      saveTheme(theme);
      syncWebPreferenceControls();
      setWebPreferenceStatus('外观已保存', 'ok');
    });
  });
  for (const id of ['web-remote-quality', 'web-remote-framerate', 'web-stats-overlay']) {
    $(id).addEventListener('change', () => setWebPreferenceStatus('有未保存的修改', ''));
  }
  $('web-settings-save')?.addEventListener('click', applyWebPreferences);
  $('account-security-login')?.addEventListener('click', () => {
    document.querySelector<HTMLButtonElement>('.tab[data-tab="account"]')?.click();
  });
  $('connection-preflight-refresh').addEventListener('click', runConnectionPreflight);

  // tabs
  const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.tab'));
  const activateTab = (tab: HTMLButtonElement, moveFocus = false): void => {
    tabs.forEach((item) => {
      const active = item === tab;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    const which = tab.dataset.tab || '';
    tabs.forEach((item) => {
      const panel = document.getElementById(`tab-${item.dataset.tab || ''}`);
      if (panel) {
        const active = item === tab;
        panel.classList.toggle('hidden', !active);
        panel.setAttribute('aria-hidden', String(!active));
      }
    });
    if (which === 'account-settings') syncWebPreferenceControls();
    if (moveFocus) tab.focus({ preventScroll: true });
  };
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activateTab(tab));
    tab.addEventListener('keydown', (event) => {
      const index = tabs.indexOf(tab);
      let next = index;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next === index) return;
      event.preventDefault();
      activateTab(tabs[next], true);
    });
  });
  const initialTab = tabs.find((tab) => tab.classList.contains('active')) || tabs[0];
  if (initialTab) activateTab(initialTab);

  $('lan-connect-btn').addEventListener('click', lanConnect);
  $('acc-login-btn').addEventListener('click', () => void accountLogin());
  $('acc-register-btn').addEventListener('click', () => void accountRegister());
  for (const id of ['acc-user', 'acc-pass']) {
    $(id).addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') void accountLogin();
    });
  }
  for (const id of ['lan-addr', 'lan-passcode']) {
    $(id).addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') lanConnect();
    });
  }
  $('account-refresh-btn').addEventListener('click', () => void loadDevices());
  $('devices-refresh-btn').addEventListener('click', () => void loadDevices());
  $('account-logout-btn').addEventListener('click', () => accountLogout());
  $('auth-sessions-refresh').addEventListener('click', () => void loadAuthSessions());
  $('auth-sessions-revoke-others').addEventListener('click', () => void revokeOtherAuthSessions());
  $('security-activity-refresh').addEventListener('click', () => void loadSecurityActivity());
  $('account-delete-btn').addEventListener('click', () => void deleteAccount());

  // 局域网历史记录
  $('lan-history-toggle').addEventListener('click', () => {
    const box = $('lan-history');
    box.classList.toggle('hidden');
    if (!box.classList.contains('hidden')) renderLanHistory();
  });
  $('lan-history').addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement;
    const addr =
      t.getAttribute('data-addr') ||
      (t.closest('[data-addr]') as HTMLElement | null)?.getAttribute('data-addr');
    if (!addr) return;
    if (t.classList.contains('hist-del')) {
      deleteLanHistory(addr);
      return;
    }
    const entry = loadLanHistory().find((e) => e.addr === addr);
    if (entry) {
      ($('lan-addr') as HTMLInputElement).value = entry.addr;
      ($('lan-passcode') as HTMLInputElement).value = entry.pass;
      $('lan-history').classList.add('hidden');
    }
  });

  $('session-disconnect').addEventListener('click', () => disconnect());
  $('session-quality').addEventListener('click', () => toggleConnectionQualityPanel());
  $('session-quality-close').addEventListener('click', () => setConnectionQualityPanelOpen(false));
  $('session-quality-copy').addEventListener('click', () => void copySessionDiagnostics());
  $('session-quality-panel').addEventListener('click', (event) => {
    if (event.target === $('session-quality-panel')) setConnectionQualityPanelOpen(false);
  });
  $('session-path').addEventListener('click', () => toggleConnectionQualityPanel());
  $('session-stream-settings').addEventListener('click', openStreamSettings);
  $('tb-stream-settings').addEventListener('click', openStreamSettings);
  $('stream-settings-close').addEventListener('click', () => closeStreamSettings());
  $('stream-settings-cancel').addEventListener('click', () => closeStreamSettings());
  $('stream-settings-apply').addEventListener('click', () => {
    applyStreamSettings();
  });
  $('stream-settings-layer').addEventListener('click', (event) => {
    if (event.target === $('stream-settings-layer')) closeStreamSettings();
  });
  $('stream-settings-layer').addEventListener('keydown', trapStreamSettingsFocus);
  $('session-controls-toggle').addEventListener('click', () => {
    setSessionControlsHidden(!appState.sessionControlsHidden);
  });
  bindFullscreenButton($('session-fullscreen') as HTMLButtonElement, $('stage'));
  const muteBtn = $('session-mute');
  muteBtn.addEventListener('click', () => {
    const v = appState.video!;
    v.muted = !v.muted;
    muteBtn.title = v.muted ? '取消静音' : '静音';
  });

  $('tb-right').addEventListener('click', () => {
    appState.rightMode = !appState.rightMode;
    $('tb-right').classList.toggle('active', appState.rightMode);
  });
  $('tb-drag').addEventListener('click', () => {
    appState.dragMode = !appState.dragMode;
    $('tb-drag').classList.toggle('active', appState.dragMode);
  });
  $('tb-copy').addEventListener('click', () => void copyRemoteClipboard());
  $('tb-shortcut').addEventListener('click', () => {
    appState.keyboardInput?.release();
    appState.shortcutMode = appState.shortcutMode === 'auto' ? 'raw' : 'auto';
    updateShortcutButton();
  });

  bindKeyboard();
  bindTextSend();
  bindFileTransfer();
  bindSessionChromeAutoHide();
  document.addEventListener('visibilitychange', syncSessionVideoVisibility);
  window.addEventListener('pageshow', syncSessionVideoVisibility);
  window.addEventListener('online', () => {
    runConnectionPreflight();
    syncSessionVideoVisibility();
  });
  window.addEventListener('offline', runConnectionPreflight);
}

// ---------- 启动 ----------
(async function main(): Promise<void> {
  appState.video = $('session-video') as HTMLVideoElement;
  // 远程桌面使用自己的视频全屏入口，不显示浏览器画中画/媒体遥控入口，
  // 避免控制过程中出现打断性的系统提示。
  appState.video.disablePictureInPicture = true;
  appState.video.controls = false;
  appState.video.setAttribute('disablepictureinpicture', '');
  appState.video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
  appState.sessionStatsOverlay = readStoredStatsOverlay();
  appState.remoteStreamSettings = readStoredRemoteStreamSettings();
  setSessionStatsOverlayEnabled(appState.sessionStatsOverlay);
  fillAddrDatalist();
  applyTheme(readTheme());
  syncWebPreferenceControls();
  bindEvents();
  runConnectionPreflight();
  bindSystemIconTheme();
  appState.pointerInput = attachPointer(appState.video, sendInput, {
    rightMode: () => appState.rightMode,
    getRemotePlatform: () => appState.remotePlatform,
  });
  appState.keyboardInput = attachKeyboard(
    sendInput,
    () => appState.sessionMode === 'control' && !$('view-session').classList.contains('hidden'),
    {
      getRemotePlatform: () => appState.remotePlatform,
      getShortcutMode: () => appState.shortcutMode,
      onPaste: pasteClipboardAndTriggerRemote,
    },
  );
  attachTouch(appState.video, sendInput, {
    rightMode: () => appState.rightMode,
    dragMode: () => appState.dragMode,
  });
  showView('connect');
  updateStreamSettingsButton();
  updateShortcutButton();
  await restoreSession();
  if (api.token) {
    void syncPersistedFileTasks();
    void loadAuthSessions();
    void loadSecurityActivity();
  }
  scheduleAccountValidation();
  void loadDownloadManifest();
})();
